/**
 * Promo codes at checkout — the pure part (spec: design/quick-specs/promo-codes.md).
 * Owns: code normalisation, refusal -> i18n key, and the "preview this purchase"
 * call with its error mapping. Must NOT: price anything itself — the discount is
 * always the server's answer.
 */
import { ApiError } from '../../core/network/api';
import { formatMoney } from '../i18n/money';
import { previewPromo, type PromoQuote, type PromoRefusal } from '../game/topup-api';

/** Business rule from Daniils, 2026-10-30 (spec "Minimum order") — full EUR price, before the code. */
export const PROMO_MIN_ORDER_CENTS = 1000;

/** "€10.00" — the rule is on the EUR price, so the copy names euros: a converted figure
 *  could sit either side of the real line (same reasoning as the KYC threshold line). */
export const minOrderLabel = (): string =>
  formatMoney(PROMO_MIN_ORDER_CENTS, { currency: 'EUR', exact: true });

/** Spec "Code format": stored upper-case, [A-Z0-9-]{3,32}. */
const CODE_FORMAT = /^[A-Z0-9-]{3,32}$/;

/** Codes are case-insensitive: what the player typed, trimmed and upper-cased. */
export const normalizeCode = (raw: string): string => raw.trim().toUpperCase();

export const isCodeShape = (code: string): boolean => CODE_FORMAT.test(code);

const REFUSALS: readonly PromoRefusal[] = [
  'not_found', 'inactive', 'not_started', 'expired', 'already_used', 'pending', 'min_order',
];

/** Translation key for a refusal reason. Unknown reasons (a newer backend) read as "not valid". */
export function refusalKey(reason: unknown): string {
  return `shop.promo.reason.${REFUSALS.includes(reason as PromoRefusal) ? reason : 'not_found'}`;
}

/** The purchase being priced — exactly one of the three, as the checkout takes them. */
export type PromoProduct = { packageId: string } | { offerId: string } | { bars: number };

export type PromoOutcome =
  | { kind: 'none' }                              // no code entered — plain price
  | { kind: 'quote'; quote: PromoQuote }
  | { kind: 'refused'; reasonKey: string }
  | { kind: 'failed'; message: string };          // could not ask (network, 5xx)

/**
 * Ask the server what `code` does to `product`. Called on every purchase click, so
 * switching product re-validates for free — and a code that expired or was used
 * since it was typed is caught before the confirm dialog, never at the gateway.
 */
export async function resolvePromo(code: string, product: PromoProduct): Promise<PromoOutcome> {
  const clean = normalizeCode(code);
  if (!clean) return { kind: 'none' };
  try {
    return { kind: 'quote', quote: await previewPromo({ code: clean, ...product }) };
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) {
      const reason = (e.details as { reason?: unknown } | undefined)?.reason;
      return { kind: 'refused', reasonKey: refusalKey(reason) };
    }
    return { kind: 'failed', message: e instanceof Error ? e.message : String(e) };
  }
}
